import React, {useState} from 'react';
import styled from 'styled-components';
import {EMPHASIS} from '@forecast-it/design-system';
import {graphql, ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import {LabelledButton} from '../../tickets/components/LabelledButton';
import {DashboardPeriodPicker} from '../components/DashboardPeriodPicker';
import {DashboardVisualizationRow} from '../components/DashboardVisualizationRow';
import {DashboardGroupedTickets} from '../components/DashboardGroupedTickets';
import {DashboardFilterPanel} from '../components/DashboardFilterPanel';
import {useDashboardFilterPanel} from '../hooks/useDashboardFilterPanel';
import {useDashboardFilterOptions} from '../hooks/useDashboardFilterOptions';
import {EmptyState} from '../../tickets/components/EmptyState';
import {DATE_PRESETS} from '../../tickets/constants/datePresets';
import type {GroupableTicket} from '../types/groupableTicket';
import type {DashboardRouteQuery as DashboardRouteQueryType} from './__generated__/DashboardRouteQuery.graphql';

const DashboardRouteQueryDoc = graphql`
	query DashboardRouteQuery($input: TicketDashboardAnalyticsInput!) {
		viewer {
			actualPersonId
			ticketDashboardAnalytics(input: $input) {
				gauge {
					achieved
					breached
					total
					ratePct
					targetPct
				}
				trend {
					date
					created
					resolved
					breached
				}
			}
		}
	}
`;

const Container = styled.div`
	margin: 0 auto;
	max-width: 80rem;
	padding: 24px;
`;

const Title = styled.h1`
	${p => p.theme.sys.font?.heading?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	margin: 0 0 24px;
`;

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 16px;
`;

// The active-state tint is layered onto the design-system Button itself, so these
// declarations follow the Button's own in the stylesheet and win — the same reason
// ColumnsToggle's trigger does it this way. A surrounding wrapper could not colour
// the trigger, because the Button always sets its own `color`.
const FilterTrigger = styled(LabelledButton)<{$active: boolean}>`
	background: ${p => (p.$active ? p.theme.sys.color.background.primary['1'] : 'transparent')};
	color: ${p => (p.$active ? p.theme.sys.color.brand.primary['1'] : p.theme.sys.color.text.content.main)};
`;

const FilterBadge = styled.span`
	display: inline-flex;
	height: 18px;
	min-width: 18px;
	align-items: center;
	justify-content: center;
	/* A count badge is neither a design-system Pill nor an Avatar, and the design
	   system has no full-round radius token — the sanctioned 9999px exception. */
	border-radius: 9999px;
	margin-left: ${p => p.theme.sys.spacing[200]};
	background: ${p => p.theme.sys.color.brand.primary['1']};
	padding: 0 5px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	color: ${p => p.theme.sys.color.text.content.ondark};
`;

const Loading = styled.div`
	padding: 16px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

/**
 * The dashboard's ticket rows come from the bulk ticket query (FOR-70), which is
 * not built yet, and are presented by the ticket data table (FOR-73). Grouping is
 * a layout over those rows, so it ships with nothing to group and its section
 * renders its own empty state until they arrive. Named as a stable constant rather
 * than a literal so the grouped section's `useMemo` isn't invalidated every render.
 */
const DASHBOARD_TICKETS: readonly GroupableTicket[] = [];

function getDefaultRange() {
	const preset = DATE_PRESETS.find(p => p.id === 'last_30_days');
	return preset ? preset.getRange() : {from: '', to: ''};
}

function activeFilterCount(applied: ReturnType<typeof useDashboardFilterPanel>['applied']): number {
	let count = 0;
	if (applied.assignee.length > 0) count++;
	if (applied.requester.length > 0) count++;
	if (applied.status.length > 0) count++;
	if (applied.priority.length > 0) count++;
	if (applied.channel.length > 0) count++;
	if (applied.tags.length > 0) count++;
	if (applied.slaState.length > 0) count++;
	if (applied.lastActivityRange.from || applied.lastActivityRange.to) count++;
	if (applied.resolvedDateRange.from || applied.resolvedDateRange.to) count++;
	if (applied.firstResponseRange.from || applied.firstResponseRange.to) count++;
	return count;
}

export function DashboardRoute() {
	const defaultRange = getDefaultRange();
	const [from, setFrom] = useState(defaultRange.from);
	const [to, setTo] = useState(defaultRange.to);
	const {isOpen, draft, applied, open, close, updateDraft, clearDraft, applyDraft, hasActiveFilters} =
		useDashboardFilterPanel();
	const filterOptions = useDashboardFilterOptions();
	const count = activeFilterCount(applied);

	return (
		<Container>
			<Title>Dashboard</Title>
			<Toolbar>
				<FilterTrigger
					$active={hasActiveFilters}
					emphasis={EMPHASIS.LOW}
					label="Filters"
					aria-haspopup="dialog"
					aria-expanded={isOpen}
					data-testid="dashboard-filter-trigger"
					onPress={open}
				>
					Filters
					{count > 0 && <FilterBadge data-testid="dashboard-filter-count">{count}</FilterBadge>}
				</FilterTrigger>
			</Toolbar>
			{/* Provisional: the visualisation row needs a period to query, so FOR-72 ships the
			    smallest control that supplies one. FOR-71 replaces this with the real dashboard
			    toolbar (date field, range presets, date pills) — delete this component then
			    rather than growing it here. */}
			<DashboardPeriodPicker from={from} to={to} onChangeFrom={setFrom} onChangeTo={setTo} />
			<ForecastQueryRenderer
				query={DashboardRouteQueryDoc}
				variables={{input: {from, to}}}
				customLoader={() => <Loading>Loading…</Loading>}
				redirectIfNotLoggedIn
				render={(relayProps: DashboardRouteQueryType['response'] | null) => {
					// `ticketDashboardAnalytics` is nullable — the account may have no ticketing
					// data to aggregate. Say so, rather than leaving the page blank below the
					// heading. (The per-card "no tickets resolved" / "no data" states live in the
					// gauge and chart; this covers having no analytics payload at all.)
					if (!relayProps?.viewer?.ticketDashboardAnalytics) {
						return (
							<EmptyState variant="inset" data-testid="dashboard-no-analytics">
								No ticket analytics available for this period.
							</EmptyState>
						);
					}
					const {gauge, trend} = relayProps.viewer.ticketDashboardAnalytics;
					return <DashboardVisualizationRow gauge={gauge} trend={[...trend]} />;
				}}
			/>
			{/* Outside the query boundary: grouping re-lays-out rows client-side and issues
			    no query of its own (PRD §13), so it neither waits on the analytics fetch nor
			    disappears when that fetch returns nothing. */}
			<DashboardGroupedTickets tickets={DASHBOARD_TICKETS} />
			<DashboardFilterPanel
				isOpen={isOpen}
				draft={draft}
				filterOptions={filterOptions}
				onClose={close}
				onApply={applyDraft}
				onUpdateDraft={updateDraft}
				onClearDraft={clearDraft}
			/>
		</Container>
	);
}
